import { useEffect, useMemo } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useToolUsageStore } from '../../../store/toolUsageStore';
import { ALL_TOOLS, PAID_SLUGS, SOON_SLUGS } from './toolCatalog';

// Which tools wear the red "Hot" badge — one rule shared by the Tools page and the dashboard.

export const HOT_PER_CATEGORY = 3; // how many tools each category may mark as hot

// Hand-picked fallback: stands in until the usage numbers arrive, and while no tool has any usage yet.
export const FALLBACK_HOT_SLUGS = new Set([
    'pdf/compress',
    'pdf/merge',
    'image/remove-background',
    'image/qr-code',
    'image/convert',
    'pdf/create',
    'image/nid-to-pdf',
]);

/**
 * "Hot" = the 3 most-used tools of each category across ALL users (last 30 days, from the server).
 * Coming-soon and paid tools carry their own badge, so they never take a hot slot.
 * @returns {Set<string>} slugs of the hot tools
 */
export function useHotToolSlugs() {
    const token = useAuthStore((s) => s.token);
    const popular = useToolUsageStore((s) => s.popular);
    const popularLoaded = useToolUsageStore((s) => s.popularLoaded);
    const loadPopular = useToolUsageStore((s) => s.loadPopular);
    useEffect(() => { if (token) loadPopular(); }, [token, loadPopular]);

    return useMemo(() => {
        if (!popularLoaded) return FALLBACK_HOT_SLUGS;
        const byCategory = {};
        for (const tool of ALL_TOOLS) {
            if (SOON_SLUGS.has(tool.slug) || PAID_SLUGS.has(tool.slug)) continue;
            const runs = popular[tool.slug] || 0;
            if (runs > 0) (byCategory[tool.categoryId] ||= []).push({ slug: tool.slug, runs });
        }
        const out = new Set();
        for (const list of Object.values(byCategory)) {
            list.sort((a, b) => b.runs - a.runs).slice(0, HOT_PER_CATEGORY).forEach((x) => out.add(x.slug));
        }
        return out.size ? out : FALLBACK_HOT_SLUGS;
    }, [popular, popularLoaded]);
}
